import React, { useState, useRef, useEffect, useCallback } from 'react';
import { NEXT_BUTTONS } from '../data/workflows';
import { STEP_RESPONSES, MODULE_WELCOME, WELCOME_MESSAGES } from '../data/responses';
import { AGENT_MODULES, BURGER_MODULES } from '../data/modules';
import { renderMarkdown } from '../utils/markdown';
import { createRecognition, createContinuousRecognition, isSTTSupported, speak, stopSpeaking } from '../utils/speech';
import { sendMessage } from '../utils/mistral';

// ═══════════════════════════════════════════════════════════════
// RECORDING PROMPTS — tells Mistral how to structure transcripts
// ═══════════════════════════════════════════════════════════════
const RECORDING_PROMPTS = {
  aangifte: {
    nl: 'Je ontvangt een transcript van een aangifte-gesprek. Structureer dit tot een concept proces-verbaal (PV). Gebruik de standaard PV-structuur: type delict, datum/tijd/locatie, verklaring aangever, signalement verdachte(n), getuigen, bewijsmateriaal. Markeer ontbrekende informatie met [ONTBREEKT].',
    en: 'You receive a transcript of a report filing. Structure this into a draft official statement (PV). Use standard PV structure: offense type, date/time/location, complainant statement, suspect description, witnesses, evidence. Mark missing info with [MISSING].',
  },
  verhoor: {
    nl: 'Je ontvangt een transcript van een politieverhoor. Structureer dit tot een verhoorverslag: identificeer spreker-wissels (verhoorder vs verdachte/getuige), markeer inconsistenties, genereer een samenvatting, en noteer relevante citaten.',
    en: 'You receive an interview transcript. Structure into an interview report: identify speaker changes (interviewer vs suspect/witness), flag inconsistencies, generate summary, note relevant quotes.',
  },
  incident: {
    nl: 'Je ontvangt een spraak-transcript van een incident. Structureer dit tot een incidentregistratie voor het BVH-systeem: wat, waar, wanneer, wie, hoe, prioriteit, categorie.',
    en: 'You receive a speech transcript of an incident. Structure into an incident registration: what, where, when, who, how, priority, category.',
  },
  _default: {
    nl: 'Je ontvangt een transcript van een opname. Structureer de informatie in een duidelijk en overzichtelijk verslag met de belangrijkste punten.',
    en: 'You receive a recording transcript. Structure the information into a clear and organized report with key points.',
  },
};

// Quick action definitions per module
const QUICK_ACTIONS = {
  aangifte: {
    nl: [
      { icon: '🎙️', label: 'Opname starten', action: 'record' },
      { icon: '📋', label: 'Genereer PV', action: 'generate_pv' },
      { icon: '⚖️', label: 'Wetsartikel', action: 'law' },
    ],
    en: [
      { icon: '🎙️', label: 'Start recording', action: 'record' },
      { icon: '📋', label: 'Generate PV', action: 'generate_pv' },
      { icon: '⚖️', label: 'Legal article', action: 'law' },
    ],
  },
  verhoor: {
    nl: [
      { icon: '🎙️', label: 'Verhoor opnemen', action: 'record' },
      { icon: '⚖️', label: 'Cautie voorlezen', action: 'rights' },
      { icon: '📄', label: 'Samenvatting', action: 'summary' },
    ],
    en: [
      { icon: '🎙️', label: 'Record interview', action: 'record' },
      { icon: '⚖️', label: 'Read rights', action: 'rights' },
      { icon: '📄', label: 'Summary', action: 'summary' },
    ],
  },
  incident: {
    nl: [
      { icon: '🎙️', label: 'Incident inspreken', action: 'record' },
      { icon: '📍', label: 'Locatie toevoegen', action: 'location' },
      { icon: '🚨', label: 'Prioriteit', action: 'priority' },
    ],
    en: [
      { icon: '🎙️', label: 'Dictate incident', action: 'record' },
      { icon: '📍', label: 'Add location', action: 'location' },
      { icon: '🚨', label: 'Priority', action: 'priority' },
    ],
  },
  _default: {
    nl: [
      { icon: '🎙️', label: 'Opname starten', action: 'record' },
      { icon: '🔍', label: 'Informatie zoeken', action: 'search' },
    ],
    en: [
      { icon: '🎙️', label: 'Start recording', action: 'record' },
      { icon: '🔍', label: 'Search info', action: 'search' },
    ],
  },
};

export default function Chat({ mode, module, lang, currentStep, setCurrentStep, ttsEnabled, messages, setMessages }) {
  const [input, setInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [interimText, setInterimText] = useState('');

  // ── RECORDING MODE STATE ──
  const [isRecording, setIsRecording] = useState(false);
  const [recordingText, setRecordingText] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);

  const chatEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const recordingRecRef = useRef(null);
  const recordingTextRef = useRef('');
  const timerRef = useRef(null);
  const inputRef = useRef(null);
  const shouldRestartRef = useRef(false);

  // Auto-scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, recordingText]);

  // Show welcome message when module changes
  useEffect(() => {
    if (!module) return;
    const welcome = MODULE_WELCOME[module];
    if (welcome) {
      setMessages([{
        role: 'ai',
        content: welcome[lang] || welcome.nl,
        timestamp: Date.now(),
      }]);
      setCurrentStep(1);
    }
  }, [module]); // eslint-disable-line

  // Show mode welcome when no module selected
  useEffect(() => {
    if (!module && mode) {
      const welcome = WELCOME_MESSAGES[mode];
      if (welcome) {
        setMessages([{
          role: 'ai',
          content: welcome[lang] || welcome.nl,
          timestamp: Date.now(),
        }]);
      }
    }
  }, [mode]); // eslint-disable-line

  // Cleanup recording on unmount
  useEffect(() => {
    return () => {
      if (recordingRecRef.current) recordingRecRef.current.stop();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Get current module info
  const allModules = { ...AGENT_MODULES, ...BURGER_MODULES };
  const currentModule = allModules[module];

  // ═══════════════════════════════════════════════════════════════
  // RECORDING MODE — Continuous transcription
  // ═══════════════════════════════════════════════════════════════
  const startRecording = useCallback(() => {
    if (!isSTTSupported()) {
      alert(lang === 'nl'
        ? 'Spraakherkenning wordt niet ondersteund in deze browser. Gebruik Chrome of Edge.'
        : 'Speech recognition is not supported in this browser. Use Chrome or Edge.');
      return;
    }

    // Reset
    recordingTextRef.current = '';
    setRecordingText('');
    setRecordingTime(0);
    shouldRestartRef.current = true;

    const recognition = createContinuousRecognition(lang);
    recordingRecRef.current = recognition;

    let finalTranscript = '';

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interim = transcript;
        }
      }
      recordingTextRef.current = finalTranscript.trim();
      setRecordingText((finalTranscript + interim).trim());
    };

    // Auto-restart if Chrome stops (it does after ~60s of silence)
    recognition.onend = () => {
      if (shouldRestartRef.current) {
        try {
          // Small delay before restart to avoid rapid cycling
          setTimeout(() => {
            if (shouldRestartRef.current && recordingRecRef.current) {
              finalTranscript = recordingTextRef.current + ' ';
              recordingRecRef.current.start();
            }
          }, 200);
        } catch (e) {
          console.warn('Could not restart recording:', e);
        }
      }
    };

    recognition.onerror = (e) => {
      if (e.error === 'no-speech') return; // normal, just no speech detected
      if (e.error === 'aborted') return;   // normal when stopping
      console.warn('Recording error:', e.error);
    };

    recognition.start();
    setIsRecording(true);

    // Timer
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);

    // Add system message
    setMessages(prev => [...prev, {
      role: 'ai',
      content: lang === 'nl'
        ? '🎙️ **Opname gestart** — Ik luister mee en transcribeer. Druk op **⏹ Stop** wanneer u klaar bent, dan structureer ik het automatisch.'
        : '🎙️ **Recording started** — I am listening and transcribing. Press **⏹ Stop** when done and I will structure it automatically.',
      timestamp: Date.now(),
    }]);
  }, [lang, setMessages]);

  const stopRecording = useCallback(async () => {
    shouldRestartRef.current = false;
    if (recordingRecRef.current) {
      recordingRecRef.current.stop();
      recordingRecRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);

    const transcript = recordingTextRef.current.trim();
    if (!transcript) {
      setRecordingText('');
      return;
    }

    // Show transcript as user message
    const label = lang === 'nl' ? '📝 Opname transcript' : '📝 Recording transcript';
    setMessages(prev => [...prev, {
      role: 'user',
      content: `${label}:\n\n${transcript}`,
      timestamp: Date.now(),
    }]);
    setRecordingText('');
    setIsTyping(true);

    // Send to Mistral for structuring
    const recPrompt = RECORDING_PROMPTS[module] || RECORDING_PROMPTS._default;
    const structurePrompt = recPrompt[lang] || recPrompt.nl;

    const apiResult = await sendMessage({
      module: module || 'kennis',
      message: `${structurePrompt}\n\n--- TRANSCRIPT ---\n${transcript}\n--- EINDE TRANSCRIPT ---`,
      lang,
      mode,
      history: [],
    });

    let responseText;
    if (apiResult.success && apiResult.content) {
      responseText = apiResult.content;
    } else {
      responseText = lang === 'nl'
        ? `📋 **Transcript ontvangen** (${transcript.split(' ').length} woorden)\n\nHet transcript is opgeslagen. In de productieversie wordt dit automatisch gestructureerd door Mistral AI.\n\n**Ruwe tekst:**\n${transcript}`
        : `📋 **Transcript received** (${transcript.split(' ').length} words)\n\nThe transcript has been saved. In the production version, this will be automatically structured by Mistral AI.\n\n**Raw text:**\n${transcript}`;
    }

    const aiMsg = { role: 'ai', content: responseText, timestamp: Date.now() };
    setTimeout(() => {
      setMessages(prev => [...prev, aiMsg]);
      setIsTyping(false);
      if (ttsEnabled) speak(responseText, lang);
    }, 600);
  }, [module, lang, mode, ttsEnabled, setMessages]);

  // ═══════════════════════════════════════════════════════════════
  // SEND MESSAGE — Chat mode (questions to AI)
  // ═══════════════════════════════════════════════════════════════
  const handleSend = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg) return;
    setInput('');

    const userMsg = { role: 'user', content: msg, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setIsTyping(true);

    const apiResult = await sendMessage({
      module: module || 'kennis',
      message: msg,
      lang,
      mode,
      history: messages.map(m => ({ role: m.role === 'ai' ? 'assistant' : m.role, content: m.content })),
    });

    let responseText;
    if (apiResult.success && apiResult.content) {
      responseText = apiResult.content;
    } else {
      const defaultResp = STEP_RESPONSES._default;
      responseText = defaultResp[lang] || defaultResp.nl;
    }

    const aiMsg = { role: 'ai', content: responseText, timestamp: Date.now() };
    setTimeout(() => {
      setMessages(prev => [...prev, aiMsg]);
      setIsTyping(false);
      if (ttsEnabled) speak(responseText, lang);
    }, 600 + Math.random() * 400);
  }, [input, module, lang, mode, messages, ttsEnabled, setMessages]);

  // ═══════════════════════════════════════════════════════════════
  // QUICK ACTIONS — Context-aware buttons
  // ═══════════════════════════════════════════════════════════════
  const handleQuickAction = useCallback((action) => {
    if (action === 'record') {
      if (isRecording) {
        stopRecording();
      } else {
        startRecording();
      }
      return;
    }

    // Other quick actions: send as a question to Mistral
    const actionMessages = {
      generate_pv: { nl: 'Genereer een concept proces-verbaal op basis van ons gesprek', en: 'Generate a draft official statement based on our conversation' },
      law: { nl: 'Welke wetsartikelen zijn van toepassing?', en: 'Which legal articles apply?' },
      rights: { nl: 'Lees de cautie voor (art. 29 Sv) en leg de rechten van de verdachte uit', en: 'Read the caution (art. 29 Sv) and explain suspect rights' },
      summary: { nl: 'Genereer een samenvatting van het gesprek tot nu toe', en: 'Generate a summary of the conversation so far' },
      location: { nl: 'Help me de locatiegegevens vast te leggen', en: 'Help me record location details' },
      priority: { nl: 'Help me de prioriteit en categorie te bepalen', en: 'Help me determine priority and category' },
      search: { nl: 'Ik wil informatie opzoeken. Waarmee kan je me helpen?', en: 'I want to look up information. How can you help?' },
    };

    const msg = actionMessages[action]?.[lang] || actionMessages[action]?.nl;
    if (msg) handleSend(msg);
  }, [isRecording, startRecording, stopRecording, handleSend, lang]);

  // ═══════════════════════════════════════════════════════════════
  // NEXT-STEP BUTTONS — Now via Mistral (with fallback)
  // ═══════════════════════════════════════════════════════════════
  const handleNextStep = useCallback(async (button) => {
    // Add user action as message
    setMessages(prev => [...prev, {
      role: 'user',
      content: button[lang] || button.nl,
      timestamp: Date.now(),
    }]);
    setIsTyping(true);

    // Try Mistral first for dynamic response
    const contextMsg = lang === 'nl'
      ? `De agent klikt op de actieknop "${button[lang] || button.nl}" in het werkproces. Geef een passend antwoord en begeleid de volgende stap.`
      : `The officer clicks the action button "${button[lang] || button.nl}" in the workflow. Provide an appropriate response and guide the next step.`;

    const apiResult = await sendMessage({
      module: module || 'kennis',
      message: contextMsg,
      lang,
      mode,
      history: messages.slice(-6).map(m => ({ role: m.role === 'ai' ? 'assistant' : m.role, content: m.content })),
    });

    let responseText;
    if (apiResult.success && apiResult.content) {
      responseText = apiResult.content;
    } else {
      // Fallback to static responses
      const moduleResponses = STEP_RESPONSES[module];
      const actionResponse = moduleResponses?.[button.action];
      responseText = actionResponse?.[lang] || actionResponse?.nl || STEP_RESPONSES._default[lang] || STEP_RESPONSES._default.nl;
    }

    setTimeout(() => {
      setMessages(prev => [...prev, {
        role: 'ai',
        content: responseText,
        timestamp: Date.now(),
      }]);
      setIsTyping(false);

      // Advance workflow step
      if (['next_step', 'approve', 'submit', 'export', 'publish', 'send_om'].includes(button.action)) {
        setCurrentStep(prev => prev + 1);
      }
      if (button.action === 'reset') {
        setCurrentStep(1);
      }
      if (button.action.startsWith('select_') || ['record', 'dictate', 'morning', 'afternoon', 'night',
        'person', 'vehicle', 'object', 'event', 'firearms', 'narcotics', 'weapons', 'valuables',
        'week', 'next_week', 'month', 'sr', 'sv', 'pw', 'violence', 'property', 'sexual',
        'burglary', 'theft', 'fraud', 'stopped', 'search', 'frisk',
        'bank', 'phishing', 'hacked', 'physical', 'financial', 'domestic'].includes(button.action)) {
        setCurrentStep(prev => prev + 1);
      }

      if (ttsEnabled) speak(responseText, lang);
    }, 600 + Math.random() * 400);
  }, [module, lang, mode, messages, ttsEnabled, setMessages, setCurrentStep]);

  // ═══════════════════════════════════════════════════════════════
  // VOICE INPUT — Single question mode (press mic, speak, auto-send)
  // ═══════════════════════════════════════════════════════════════
  const toggleListening = useCallback(() => {
    if (isRecording) return; // don't interfere with recording mode

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      setInterimText('');
      return;
    }

    if (!isSTTSupported()) {
      alert(lang === 'nl' ? 'Spraakherkenning wordt niet ondersteund in deze browser.' : 'Speech recognition is not supported in this browser.');
      return;
    }

    const recognition = createRecognition(lang);
    recognitionRef.current = recognition;

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }
      if (final) {
        setInput(prev => prev + final);
        setInterimText('');
      } else {
        setInterimText(interim);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimText('');
      if (inputRef.current?.value) {
        setTimeout(() => handleSend(inputRef.current.value), 300);
      }
    };

    recognition.onerror = (e) => {
      console.warn('Speech error:', e.error);
      setIsListening(false);
      setInterimText('');
    };

    recognition.start();
    setIsListening(true);
  }, [isListening, isRecording, lang, handleSend]);

  // Keyboard handler
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Get next-step buttons for current module and step
  const nextButtons = module && NEXT_BUTTONS[module]?.[currentStep];

  // Get quick actions for current module
  const quickActions = module
    ? (QUICK_ACTIONS[module]?.[lang] || QUICK_ACTIONS._default[lang])
    : null;

  // Speak individual message
  const speakMessage = (text) => {
    stopSpeaking();
    speak(text, lang);
  };

  // Format timestamp
  const formatTime = (ts) => {
    return new Date(ts).toLocaleTimeString(lang === 'nl' ? 'nl-NL' : 'en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format recording time
  const formatRecTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <>
      {/* Module Header */}
      {currentModule && (
        <div className="module-header">
          <span className="module-icon">{currentModule.icon}</span>
          <span className="module-name">{currentModule[lang]}</span>
          <div className="module-status">
            <span className="status-dot" />
            <span>ONLINE</span>
          </div>
        </div>
      )}

      {/* Chat Messages */}
      <div className="chat-area">
        {messages.map((msg, i) => (
          <div key={i} className={`message ${msg.role === 'user' ? 'user' : 'ai'}`}>
            <div className="message-avatar">
              {msg.role === 'user' ? '👤' : '🚔'}
            </div>
            <div>
              <div
                className="message-content"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
              />
              <div className="message-time">
                {formatTime(msg.timestamp)}
                {msg.role === 'ai' && (
                  <button className="message-tts" onClick={() => speakMessage(msg.content)} title="🔊">
                    🔊
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Recording live transcript */}
        {isRecording && recordingText && (
          <div className="message user">
            <div className="message-avatar">🎤</div>
            <div>
              <div className="message-content recording-live">
                {recordingText}
                <span className="recording-cursor">|</span>
              </div>
              <div className="message-time" style={{ color: '#ef4444' }}>
                {lang === 'nl' ? 'Live transcriptie...' : 'Live transcription...'}
              </div>
            </div>
          </div>
        )}

        {/* Typing indicator */}
        {isTyping && (
          <div className="message ai">
            <div className="message-avatar">🚔</div>
            <div className="message-content">
              <div className="typing-indicator">
                <div className="typing-dot" />
                <div className="typing-dot" />
                <div className="typing-dot" />
              </div>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Recording Bar */}
      {isRecording && (
        <div className="recording-bar">
          <div className="recording-dot" />
          <span className="recording-label">
            {lang === 'nl' ? 'OPNAME ACTIEF' : 'RECORDING'}
          </span>
          <span className="recording-timer">{formatRecTime(recordingTime)}</span>
          <span className="recording-words">
            {recordingText.split(' ').filter(Boolean).length} {lang === 'nl' ? 'woorden' : 'words'}
          </span>
          <button className="recording-stop" onClick={stopRecording}>
            ⏹ {lang === 'nl' ? 'Stop & Verwerk' : 'Stop & Process'}
          </button>
        </div>
      )}

      {/* Quick Action Buttons */}
      {quickActions && !isRecording && (
        <div className="quick-actions">
          {quickActions.map((qa, i) => (
            <button
              key={i}
              className={`quick-action-btn${qa.action === 'record' && isRecording ? ' active' : ''}`}
              onClick={() => handleQuickAction(qa.action)}
            >
              {qa.icon} {qa.label}
            </button>
          ))}
        </div>
      )}

      {/* Next-step buttons */}
      {nextButtons && nextButtons.length > 0 && !isRecording && (
        <div className="next-steps">
          {nextButtons.map((btn, i) => (
            <button
              key={i}
              className="next-step-btn"
              onClick={() => handleNextStep(btn)}
            >
              {btn[lang] || btn.nl}
            </button>
          ))}
        </div>
      )}

      {/* Input bar */}
      {!isRecording && (
        <div className="input-bar">
          <button
            className={`mic-btn${isListening ? ' listening' : ''}`}
            onClick={toggleListening}
            title={lang === 'nl' ? 'Stel een vraag via spraak' : 'Ask a question by voice'}
          >
            🎤
          </button>

          {isListening ? (
            <div className="waveform">
              {Array.from({ length: 20 }).map((_, i) => (
                <div
                  key={i}
                  className="wave-bar"
                  style={{ animationDelay: `${i * 0.05}s`, height: `${4 + Math.random() * 16}px` }}
                />
              ))}
            </div>
          ) : (
            <input
              ref={inputRef}
              className="input-field"
              type="text"
              value={interimText ? `${input}${interimText}` : input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                lang === 'nl'
                  ? (module ? 'Typ een bericht of gebruik spraak...' : 'Selecteer een module om te beginnen...')
                  : (module ? 'Type a message or use speech...' : 'Select a module to begin...')
              }
              disabled={!module}
            />
          )}

          <button
            className="send-btn"
            onClick={() => handleSend()}
            disabled={!input.trim() || !module}
          >
            {lang === 'nl' ? 'Verstuur' : 'Send'}
          </button>
        </div>
      )}
    </>
  );
}
